import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';

const getPageTitle = (pathname: string, dataSourceName?: string): { title: string; description?: string } => {
  if (pathname === '/dashboard') return { title: 'Dashboard', description: 'Monitor your UTM tracking health and manage your analytics properties' };
  if (pathname === '/data-sources') return { title: 'Analytics Properties', description: 'Manage all connected analytics properties' };
  if (pathname.startsWith('/data-sources/') && pathname.endsWith('/health')) {
    return {
      title: dataSourceName || 'Health Report',
      description: 'Detailed UTM tracking health analysis'
    };
  }
  if (pathname === '/alert-settings') return { title: 'Alert Settings', description: 'Configure notifications and alerts' };
  if (pathname === '/settings') return { title: 'Settings', description: 'Manage your account and preferences' };
  if (pathname === '/settings/profile') return { title: 'Profile Settings', description: 'Manage your personal information' };
  if (pathname === '/settings/workspace') return { title: 'Workspace Settings', description: 'Manage your workspace' };
  if (pathname === '/settings/billing') return { title: 'Billing Settings', description: 'Manage your subscription and billing' };
  if (pathname === '/workspace/members') return { title: 'Team Members', description: 'Manage who has access to this workspace' };
  if (pathname === '/admin') return { title: 'Admin Panel', description: 'System administration and management' };
  if (pathname === '/connect-data-source') return { title: 'Connect Analytics Property', description: 'Add a new analytics property to your workspace' };
  return { title: 'Dashboard' };
};

function HeaderContent() {
  const location = useLocation();
  const params = useParams();
  const { isMobile, toggleSidebar } = useSidebar();

  // Fetch data source name if on health page
  const { data: dataSource } = useQuery({
    queryKey: ['data-source-name', params.id],
    queryFn: async () => {
      if (!params.id) return null;
      const { data, error } = await supabase
        .from('data_sources')
        .select('name')
        .eq('id', params.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!params.id && location.pathname.includes('/health'),
  });

  const { title } = getPageTitle(location.pathname, dataSource?.name);

  return (
    <header className="border-b border-border/60 flex items-center gap-4 px-4 sm:px-6 h-[73px] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10 shrink-0">
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="hover:bg-muted/50 transition-colors"
          title="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}
      <h1 className="text-xl sm:text-2xl font-bold truncate">{title}</h1>
    </header>
  );
}

export function DashboardLayout() {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-muted/30">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <HeaderContent />
          <main className="flex-1 overflow-x-hidden">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
